import React from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  FileText,
  CheckSquare,
  Bell,
  Mail,
  Clock,
  Inbox,
  Users,
  Globe,
  MessageSquare,
  ChevronRight,
  ArrowLeft
} from 'lucide-react';
import { useAttorney } from '../../context/AttorneyContext';

/**
 * Attorney Sidebar Component
 * Navigation sidebar for attorney dashboard - styled to match admin dashboard
 */
function AttorneySidebar() {
  const { t } = useTranslation('attorney');
  const {
    isAdmin,
    isInternal,
    canMessageClients,
    canProcessInternalRequests
  } = useAttorney();

  // Base nav items for all attorneys
  const navItems = [
    {
      path: '/attorney/filing-queue',
      icon: FileText,
      label: t('attorney.nav.filingQueue', 'Filing Queue'),
      description: 'Pending trademark filings'
    },
    {
      path: '/attorney/approval-queue',
      icon: CheckSquare,
      label: t('attorney.nav.approvalQueue', 'Approval Queue'),
      description: 'AI drafts for review'
    },
    ...(canMessageClients ? [{
      path: '/attorney/client-messages',
      icon: Bell,
      label: t('attorney.nav.notifications', 'Notifications'),
      description: 'Messages and alerts'
    }] : []),
    ...(!isInternal ? [{
      path: '/attorney/markotm-responses',
      icon: MessageSquare,
      label: t('attorney.nav.markotmResponses', 'MarkoTM Responses'),
      description: 'Responses from MarkoTM'
    }] : []),
    {
      path: '/attorney/correspondence',
      icon: Mail,
      label: t('attorney.nav.correspondence', 'Correspondence'),
      description: 'Email communications'
    },
    {
      path: '/attorney/deadlines',
      icon: Clock,
      label: t('attorney.nav.deadlines', 'Deadlines'),
      description: 'Upcoming due dates'
    }
  ];

  // Internal requests queue - only for MarkoTM attorneyadmins
  const attorneyAdminItems = canProcessInternalRequests ? [
    {
      path: '/attorney/internal-requests',
      icon: Inbox,
      label: t('attorney.nav.internalRequests', 'Internal Requests'),
      description: 'Requests from external firms'
    }
  ] : [];

  const adminItems = [
    ...(isInternal ? [{
      path: '/attorney/clients',
      icon: Users,
      label: t('attorney.nav.clients', 'My Clients'),
      description: 'Client management'
    }] : []),
    {
      path: '/attorney/my-team',
      icon: Users,
      label: t('attorney.nav.myTeam', 'My Team'),
      description: 'Team members'
    },
    {
      path: '/attorney/jurisdictions',
      icon: Globe,
      label: t('attorney.nav.jurisdictions', 'Jurisdictions'),
      description: 'IP office information'
    }
  ];

  const renderNavItem = (item) => {
    const Icon = item.icon;
    return (
      <NavLink
        key={item.path}
        to={item.path}
        className={({ isActive }) => `
          w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-all duration-200
          ${isActive
            ? 'bg-[#004d40] text-white shadow-md'
            : 'text-gray-700 hover:bg-gray-50'
          }
        `}
      >
        {({ isActive }) => (
          <>
            <div className={`
              w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0
              ${isActive ? 'bg-white/20' : 'bg-[#e0f2f1]'}
            `}>
              <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-[#00796b]'}`} />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-sm font-medium ${isActive ? 'text-white' : ''}`}>
                  {item.label}
                </span>
              </div>
              <span className={`text-xs ${isActive ? 'text-white/70' : 'text-gray-500'}`}>
                {item.description}
              </span>
            </div>

            {isActive && (
              <ChevronRight className="w-4 h-4 text-white/60 flex-shrink-0" />
            )}
          </>
        )}
      </NavLink>
    );
  };

  return (
    <aside className="w-64 bg-white border-r border-gray-200 shadow-sm flex-shrink-0 fixed left-0 top-16 bottom-0 flex flex-col">
      <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
        {navItems.map(renderNavItem)}

        {attorneyAdminItems.length > 0 && (
          <>
            <div className="my-3 border-t border-gray-200" />
            {attorneyAdminItems.map(renderNavItem)}
          </>
        )}

        {isAdmin && (
          <>
            <div className="my-3 border-t border-gray-200" />
            {adminItems.map(renderNavItem)}
          </>
        )}
      </nav>

      {/* Footer - Client Dashboard Link */}
      <div className="p-3 border-t border-gray-200">
        <Link
          to="/dashboard"
          className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('attorney.nav.clientDashboard', 'Client Dashboard')}</span>
        </Link>
      </div>
    </aside>
  );
}

export default AttorneySidebar;
